import { Spacing } from '@persona-id/design-tokens';
import { MoreIcon, PlusIcon } from '@persona-id/design-tokens/icons';
import { cloneDeep } from 'lodash';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import FormEditSARNarrativeTemplate, {
  FormEditSARNarrativeTemplateData,
} from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/FormEditSARNarrativeTemplate';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import TableLayout, { TableTD, TableTH, TableTR } from 'dashboard/layouts/TableLayout';
import {
  generateSARNarrativeTemplateToken,
  SARNarrativeTemplate,
} from 'dashboard/models/case-template';
import Button from 'design-system/components/Button';
import DropdownMenu from 'design-system/components/DropdownMenu';
import Spacer from 'design-system/components/Spacer';
import { Heading } from 'design-system/components/Text';
import { DATE_AND_TIME_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useToggle from 'lib/hooks/useToggle';

const StyledTableTD = styled(TableTD)`
  width: 100%;
`;

const NarrativeTemplatesContainer = styled('div')`
  border-top: 1px solid ${({ theme }) => theme.color.background.secondary};
  padding: ${Spacing[5]};
`;

const NarrativeTemplateHeader = styled('div')`
  display: grid;
  grid-auto-flow: column;
  grid-column-gap: ${Spacing[2]};
  grid-template-columns: 2fr 1fr;
  align-items: center;
`;

export interface SARPanelProps {
  sarNarrativeTemplates: SARNarrativeTemplate[];
  updateSARNarrativeTemplates: (templates: SARNarrativeTemplate[]) => void;
}

const SARPanel = ({ sarNarrativeTemplates, updateSARNarrativeTemplates }: SARPanelProps) => {
  const enableSARNarrativeTemplates = useFeatureFlag('dashboard:enable-sar-narrative-template');

  const [
    addNarrativeTemplateModal,
    { enable: showAddNarrativeTemplateModal, disable: hideAddNarrativeTemplateModal },
  ] = useToggle(false);
  const [
    editNarrativeTemplateModal,
    { enable: showEditNarrativeTemplateModal, disable: hideEditNarrativeTemplateModal },
  ] = useToggle(false);
  const [currentNarrativeTemplate, setCurrentNarrativeTemplate] = useState<
    SARNarrativeTemplate | undefined
  >(undefined);
  const handleDeleteNarrativeTemplate = (token: string) => {
    const newTemplates = sarNarrativeTemplates.filter((template) => template.token !== token);
    updateSARNarrativeTemplates(newTemplates);
  };

  const allNarrativeTemplateNames = useMemo(() => {
    return sarNarrativeTemplates.map((template) => template.name);
  }, [sarNarrativeTemplates]);

  if (!enableSARNarrativeTemplates) {
    return null;
  }

  const moreItems = (narrativeTemplate: SARNarrativeTemplate) => {
    return [
      {
        id: 'sar-panel-templates__edit_button',
        label: 'Edit',
        variant: 'basic' as const,
        onClick: () => {
          setCurrentNarrativeTemplate(narrativeTemplate);
          showEditNarrativeTemplateModal();
        },
      },
      {
        id: 'sar-panel-templates__delete_button',
        label: 'Delete',
        variant: 'danger' as const,
        onClick: () => handleDeleteNarrativeTemplate(narrativeTemplate.token),
      },
    ];
  };

  const handleUpdateNarrativeTemplate = (
    narrativeTemplateData: FormEditSARNarrativeTemplateData,
  ) => {
    if (!currentNarrativeTemplate) {
      return;
    }

    const nextTemplates = cloneDeep(sarNarrativeTemplates);
    const narrativeIndex = nextTemplates.findIndex(
      (t) => t.token === currentNarrativeTemplate.token,
    );
    nextTemplates[narrativeIndex] = {
      ...nextTemplates[narrativeIndex],
      ...narrativeTemplateData,
      'updated-at': new Date().toISOString(),
    };

    updateSARNarrativeTemplates(nextTemplates);
    hideEditNarrativeTemplateModal();
  };

  const handleAddNarrativeTemplate = (narrativeTemplateData: FormEditSARNarrativeTemplateData) => {
    const nextTemplates = [
      ...sarNarrativeTemplates,
      {
        ...narrativeTemplateData,
        token: generateSARNarrativeTemplateToken(),
        'updated-at': new Date().toISOString(),
      },
    ];
    updateSARNarrativeTemplates(nextTemplates);
    hideAddNarrativeTemplateModal();
  };

  return (
    <>
      <NarrativeTemplatesContainer>
        <NarrativeTemplateHeader>
          <Heading size='sm'>Narrative templates</Heading>
          <Button
            id='sar-panel__add-narrative-template'
            prefixIcon={<PlusIcon />}
            variant='inverse'
            width='auto'
            onClick={showAddNarrativeTemplateModal}
          >
            Add
          </Button>
        </NarrativeTemplateHeader>
        <Spacer height={1} />
        <TableLayout
          showHeader={sarNarrativeTemplates.length > 0}
          isEmpty={sarNarrativeTemplates.length === 0}
          nullState={<></>}
          headerTR={
            <>
              <TableTH>Narrative</TableTH>
              <TableTH>{`Last edited (${DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})`}</TableTH>
              <TableTH></TableTH>
            </>
          }
        >
          {sarNarrativeTemplates.map((template) => {
            return (
              <TableTR key={template.token}>
                <StyledTableTD>{template.name}</StyledTableTD>
                <StyledTableTD>
                  {DateTime.fromISO(template['updated-at']).toFormat(DATE_AND_TIME_FORMAT)}
                </StyledTableTD>
                <StyledTableTD>
                  <DropdownMenu
                    trigger={
                      <Button
                        id='sar-panel-row__dropdown'
                        variant='inverse'
                        width='icon'
                        aria-label='More'
                      >
                        <MoreIcon />
                      </Button>
                    }
                  >
                    {moreItems(template).map((item) => (
                      <DropdownMenu.Item
                        id={item.id}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          item.onClick();
                        }}
                        key={item.id}
                        variant={item.variant}
                      >
                        {item.label}
                      </DropdownMenu.Item>
                    ))}
                  </DropdownMenu>
                </StyledTableTD>
              </TableTR>
            );
          })}
        </TableLayout>
      </NarrativeTemplatesContainer>
      {currentNarrativeTemplate && (
        <ModalWrapper
          isOpen={editNarrativeTemplateModal}
          handleCloseModal={hideEditNarrativeTemplateModal}
        >
          <ModalContainer size='lg'>
            <ModalHeader onClose={hideEditNarrativeTemplateModal}>
              Edit narrative template
            </ModalHeader>
            <ModalBody>
              <FormEditSARNarrativeTemplate
                defaultValues={{
                  name: currentNarrativeTemplate.name,
                  body: currentNarrativeTemplate.body,
                }}
                allNarrativeTemplateNames={allNarrativeTemplateNames.filter(
                  (name) => name !== currentNarrativeTemplate.name,
                )}
                handleUpdateNarrativeTemplate={handleUpdateNarrativeTemplate}
                handleClose={hideEditNarrativeTemplateModal}
              />
            </ModalBody>
          </ModalContainer>
        </ModalWrapper>
      )}
      <ModalWrapper
        isOpen={addNarrativeTemplateModal}
        handleCloseModal={hideAddNarrativeTemplateModal}
      >
        <ModalContainer size='lg'>
          <ModalHeader onClose={hideAddNarrativeTemplateModal}>Add narrative template</ModalHeader>
          <ModalBody>
            <FormEditSARNarrativeTemplate
              allNarrativeTemplateNames={allNarrativeTemplateNames}
              handleUpdateNarrativeTemplate={handleAddNarrativeTemplate}
              handleClose={hideAddNarrativeTemplateModal}
            />
          </ModalBody>
        </ModalContainer>
      </ModalWrapper>
    </>
  );
};

export default SARPanel;
